import { z } from 'zod';

import {
  ReturnResolutionInput,
  type PostAdminV1StoresStoreIdReturnsIdResolveBody,
} from '@nucleus-commerce/api-client/admin';
import { isMoney, type Money } from '@nucleus-commerce/core';

import { isMoneyResolution } from './return-rules';

const moneyValue = z.custom<Money>(isMoney).nullable();

/**
 * Amount and fee only matter when money moves; the other resolutions are recorded as they are.
 * Both are capped at the returned units' value, the way the backend caps them (422 on
 * `body.amount` / `body.restockingFee` with `meta.max`).
 */
export function resolveSchema(returnedValue: Money) {
  return z
    .object({
      resolution: z.enum(ReturnResolutionInput),
      amount: moneyValue,
      restockingFee: moneyValue,
      restock: z.boolean(),
      note: z.string().trim().max(2000, { error: 'returns:validation.noteLength' }),
    })
    .superRefine((v, ctx) => {
      if (!isMoneyResolution(v.resolution)) return;
      if (v.amount === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'returns:validation.amountRequired',
        });
      } else if (v.amount.amount <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'returns:validation.amountPositive',
        });
      } else if (v.amount.amount > returnedValue.amount) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'returns:validation.amountOverValue',
        });
      }
      if (v.restockingFee && v.restockingFee.amount < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['restockingFee'],
          message: 'returns:validation.feeNegative',
        });
      } else if (v.restockingFee && v.restockingFee.amount > returnedValue.amount) {
        ctx.addIssue({
          code: 'custom',
          path: ['restockingFee'],
          message: 'returns:validation.feeOverValue',
        });
      }
    });
}

export type ResolveFormInput = z.input<ReturnType<typeof resolveSchema>>;
export type ResolveFormOutput = z.output<ReturnType<typeof resolveSchema>>;

export function toResolveBody(
  values: ResolveFormOutput,
): PostAdminV1StoresStoreIdReturnsIdResolveBody {
  const movesMoney = isMoneyResolution(values.resolution);
  return {
    resolution: values.resolution,
    restock: values.restock,
    ...(movesMoney && values.amount ? { amount: values.amount } : {}),
    ...(movesMoney && values.restockingFee ? { restockingFee: values.restockingFee } : {}),
    ...(values.note ? { note: values.note } : {}),
  };
}
